import { useState, useEffect, useCallback, useMemo } from 'react';
import { getAllActivityLogs, updateActivityLog, deleteActivityLog } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { useRole } from '../../components/RoleGuard';
import { format } from 'date-fns';
import DataTable from '../../components/DataTable';

function EditModal({ log, onClose, onSave, saving }) {
  const [formData, setFormData] = useState(log || {});

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    await onSave(formData);
    onClose();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: 'var(--card-bg)', borderRadius: 14, width: '100%', maxWidth: 500, boxShadow: '0 24px 48px rgba(0,0,0,0.25)', flexShrink: 0 }}>
        <div style={{ padding: '18px 22px', borderBottom: '0.5px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>Edit Activity Log Entry</div>
          <button style={{ background: 'none', border: 'none', fontSize: 16, cursor: 'pointer', color: 'var(--text3)' }} onClick={onClose}>✕</button>
        </div>
        
        <div style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text3)', display: 'block', marginBottom: 4 }}>Activity Type</label>
            <input type="text" value={formData.activity_type || ''} onChange={e => handleChange('activity_type', e.target.value)}
              style={{ width: '100%', padding: '8px 10px', fontSize: 13, borderRadius: 7, border: '0.5px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontFamily: 'inherit' }} />
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text3)', display: 'block', marginBottom: 4 }}>Notes</label>
            <textarea value={formData.notes || ''} onChange={e => handleChange('notes', e.target.value)}
              style={{ width: '100%', padding: '8px 10px', fontSize: 13, borderRadius: 7, border: '0.5px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontFamily: 'inherit', minHeight: 100, resize: 'vertical' }} />
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text3)', display: 'block', marginBottom: 4 }}>New Status (optional)</label>
            <input type="text" value={formData.new_status || ''} onChange={e => handleChange('new_status', e.target.value)}
              style={{ width: '100%', padding: '8px 10px', fontSize: 13, borderRadius: 7, border: '0.5px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontFamily: 'inherit' }} />
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text3)', display: 'block', marginBottom: 4 }}>Previous Status (optional)</label>
            <input type="text" value={formData.previous_status || ''} onChange={e => handleChange('previous_status', e.target.value)}
              style={{ width: '100%', padding: '8px 10px', fontSize: 13, borderRadius: 7, border: '0.5px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontFamily: 'inherit' }} />
          </div>
        </div>

        <div style={{ padding: '12px 22px', borderTop: '0.5px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={saving} onClick={handleSave}>{saving ? 'Saving…' : 'Save Changes'}</button>
        </div>
      </div>
    </div>
  );
}

function DeleteModal({ log, onClose, onConfirm, deleting }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: 'var(--card-bg)', borderRadius: 14, width: '100%', maxWidth: 400, boxShadow: '0 24px 48px rgba(0,0,0,0.25)', flexShrink: 0 }}>
        <div style={{ padding: '18px 22px', borderBottom: '0.5px solid var(--border)' }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>Delete Activity Log Entry?</div>
        </div>
        
        <div style={{ padding: '18px 22px' }}>
          <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 12 }}>This action cannot be undone. The following entry will be permanently deleted:</p>
          <div style={{ background: 'var(--bg2)', border: '0.5px solid var(--border)', borderRadius: 7, padding: 12, marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>{log.activity_type || log.new_status}</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 6 }}>{log.notes}</div>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>{format(new Date(log.created_at), 'MMM d, yyyy h:mm a')}</div>
          </div>
        </div>

        <div style={{ padding: '12px 22px', borderTop: '0.5px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-danger" disabled={deleting} onClick={onConfirm}>{deleting ? 'Deleting…' : 'Delete Entry'}</button>
        </div>
      </div>
    </div>
  );
}

export default function ActivityLogs() {
  const { profile } = useAuth();
  const { canDelete } = useRole();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [editingLog, setEditingLog] = useState(null);
  const [deletingLog, setDeletingLog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [message, setMessage] = useState('');
  const [sortColumn, setSortColumn] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => { loadLogs(); }, []);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    const { data, error } = await getAllActivityLogs(1000);
    if (error) {
      setMessage(`Error loading logs: ${error.message}`);
      setLoading(false);
      return;
    }
    setLogs(data || []);
    setLoading(false);
  }, []);

  const handleEdit = async (updatedLog) => {
    setSaving(true);
    const { error } = await updateActivityLog(editingLog.id, {
      ...updatedLog,
      updated_by_id: profile?.id,
      updated_by_name: profile?.full_name,
    });
    if (error) {
      setMessage(`Error saving: ${error.message}`);
      setSaving(false);
      return;
    }
    setMessage('Activity log entry updated successfully');
    setEditingLog(null);
    setSaving(false);
    loadLogs();
  };

  const handleDelete = async () => {
    setDeleting(true);
    const { error } = await deleteActivityLog(deletingLog.id);
    if (error) {
      setMessage(`Error deleting: ${error.message}`);
      setDeleting(false);
      return;
    }
    setMessage('Activity log entry deleted successfully');
    setDeletingLog(null);
    setDeleting(false);
    loadLogs();
  };

  const filtered = logs.filter(log => {
    const matchesSearch = !searchTerm || 
      log.activity_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.notes?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.client_ref?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.performed_by_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !filterType || log.activity_type === filterType;
    return matchesSearch && matchesType;
  }).sort((a, b) => {
    let aVal = a[sortColumn];
    let bVal = b[sortColumn];
    if (aVal == null) aVal = '';
    if (bVal == null) bVal = '';
    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const sorting = useMemo(() => sortColumn ? [{ id: sortColumn, desc: sortDirection === 'desc' }] : [], [sortColumn, sortDirection]);
  const handleSortingChange = (updaterOrValue) => {
    const next = typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;
    if (!next.length) return;
    setSortColumn(next[0].id);
    setSortDirection(next[0].desc ? 'desc' : 'asc');
  };

  const activityTypes = [...new Set(logs.map(l => l.activity_type).filter(Boolean))].sort();

  const columns = [
    { id: 'client_ref', accessorKey: 'client_ref', header: 'Client Ref', size: 130, meta: { mono: true, mobile: 'secondary' }, cell: info => info.getValue() || '—' },
    {
      id: 'activity_type', accessorKey: 'activity_type', header: 'Type', size: 150,
      meta: { mobile: 'primary' },
      cell: info => info.getValue() ? <span style={{ padding: '2px 8px', borderRadius: 6, background: 'var(--accent-bg)', color: 'var(--accent)', fontSize: 11 }}>{info.getValue()}</span> : null,
    },
    { id: 'notes', accessorKey: 'notes', header: 'Notes', size: 260, meta: { truncate: true, mobile: 'hidden' }, cell: info => info.getValue() || '—' },
    { id: 'performed_by_name', accessorKey: 'performed_by_name', header: 'Created By', size: 150, meta: { mobile: 'secondary' }, cell: info => info.getValue() || '—' },
    {
      id: 'created_at', accessorKey: 'created_at', header: 'Date', size: 170, meta: { mobile: 'primary' },
      cell: info => {
        const log = info.row.original;
        return (
          <div>
            <div>{format(new Date(info.getValue()), 'MMM d, yyyy h:mm a')}</div>
            {log.updated_at && (
              <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>
                Edited by {log.updated_by_name || 'someone'} · {format(new Date(log.updated_at), 'MMM d, h:mm a')}
              </div>
            )}
          </div>
        );
      },
    },
    {
      id: 'actions', header: 'Actions', size: 130, enableSorting: false,
      meta: { align: 'right', mobile: 'secondary' },
      cell: info => {
        const log = info.row.original;
        return (
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
            <button className="btn btn-sm" style={{ background: 'var(--accent-bg)', color: 'var(--accent)', border: 'none', padding: '4px 8px', fontSize: 11 }} onClick={() => setEditingLog(log)}>Edit</button>
            {canDelete && <button className="btn btn-sm" style={{ background: 'var(--danger-bg)', color: 'var(--danger)', border: 'none', padding: '4px 8px', fontSize: 11 }} onClick={() => setDeletingLog(log)}>Delete</button>}
          </div>
        );
      },
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 16 }}>
      {/* Header */}
      <div>
        <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>Activity Log Management</div>
        <div style={{ fontSize: 13, color: 'var(--text3)' }}>View and manage all system activity logs. Edits and deletes are tracked with who made them.</div>
      </div>

      {/* Message */}
      {message && (
        <div style={{ padding: '10px 14px', borderRadius: 7, background: message.includes('Error') ? 'var(--danger-bg)' : 'var(--success-bg)', 
          color: message.includes('Error') ? 'var(--danger)' : 'var(--success)', fontSize: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{message}</span>
          <button style={{ background: 'none', border: 'none', fontSize: 14, cursor: 'pointer', color: 'inherit' }} onClick={() => setMessage('')}>✕</button>
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <input type="text" placeholder="Search activity logs…" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
          style={{ flex: 1, minWidth: 200, padding: '8px 10px', fontSize: 13, borderRadius: 7, border: '0.5px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontFamily: 'inherit' }} />
        
        <select value={filterType} onChange={e => setFilterType(e.target.value)}
          style={{ padding: '8px 10px', fontSize: 13, borderRadius: 7, border: '0.5px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontFamily: 'inherit' }}>
          <option value="">All types</option>
          {activityTypes.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>

        <button className="btn btn-primary btn-sm" onClick={loadLogs} style={{ minWidth: 100 }}>Refresh</button>
      </div>

      {/* Table */}
      <div style={{ flex: 1, minHeight: 0, border: '0.5px solid var(--border)', borderRadius: 7, background: 'var(--card-bg)' }}>
        <DataTable
          tableId="activity-logs"
          columns={columns}
          data={filtered}
          getRowId={log => log.id}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          manualSorting
          loading={loading}
          emptyMessage="No activity logs found"
          maxHeight="calc(100vh - 300px)"
        />
      </div>

      {/* Modals */}
      {editingLog && <EditModal log={editingLog} onClose={() => setEditingLog(null)} onSave={handleEdit} saving={saving} />}
      {deletingLog && <DeleteModal log={deletingLog} onClose={() => setDeletingLog(null)} onConfirm={handleDelete} deleting={deleting} />}
    </div>
  );
}
